import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-05-25 21:10:12 +0200 (Mon, 25 May 2026) $
//  Author    : Albert Charlier, Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163262 $
//  Purpose   : navigation elements
// =============================================================================
// ---Third-party imports
import React, { Fragment, useRef, useState, useEffect } from 'react';
import { Header, Divider, Icon, Menu, Item, Dropdown, Pagination, Rail, Segment, Sticky, Loader } from 'semantic-ui-react';
// ---Framework imports
import { entries, groupBy, sorted, size, range, has, flatten, keys } from 'ldash';
import { funcPart } from 'uix/parts';
import { Actions } from 'uix/registries';
import log from 'uix/logging';
import connect from 'uix/connect';
import styles from 'uix/styles';
import { Link, LargeThumb } from 'uix/components';
import { Facets } from 'uix/frontend/components';
import { useSelector } from 'uix/hooks';
import { TitleGrid } from 'uix/widgets/titleGrid';
import { formatDate } from 'uix/utils/date';
// ---Globals
export { ProtoHeader } from './headers';
let BoxHeaderIcon = ({ name })=>/*#__PURE__*/ _jsxs(Header, {
        as: "h4",
        children: [
            /*#__PURE__*/ _jsx(Icon, {
                name: "folder outline"
            }),
            /*#__PURE__*/ _jsx(Header.Content, {
                children: name
            })
        ]
    });
let ListItem = connect('ListItem', [
    'meta.request',
    'config.domains'
], ({ data, request, domains })=>{
    let desc = (name, value)=>{
        if (name == 'published') {
            return /*#__PURE__*/ _jsxs(Item.Description, {
                children: [
                    "In: ",
                    value
                ]
            }, name);
        }
        return /*#__PURE__*/ _jsx(Item.Description, {
            children: value
        }, name);
    };
    let fields = [
        'subtitle',
        'author',
        'published',
        'origin'
    ];
    let descriptions = fields.filter((n)=>data.mods[n]).map((n)=>desc(n, data.mods[n]));
    let dname = domains.odid2name[data.odid];
    return /*#__PURE__*/ _jsxs(Item, {
        children: [
            /*#__PURE__*/ _jsx(LargeThumb, {
                pageid: data.op_ot_id,
                imgheight: data.op_imgheight,
                imgwidth: data.op_imgwidth
            }),
            /*#__PURE__*/ _jsxs(Item.Content, {
                children: [
                    /*#__PURE__*/ _jsx(Item.Content, {
                        className: styles.cut,
                        style: {
                            fontSize: '1.1em'
                        },
                        children: /*#__PURE__*/ _jsx(Link, {
                            request: {
                                dname,
                                classname: 'tree',
                                tabname: 'viewer',
                                id: data.otid
                            },
                            children: data.mods.title
                        })
                    }),
                    descriptions
                ]
            })
        ]
    }, data.otid);
});
const DayGroupedTitles = ({ rows })=>{
    let byDay = groupBy(rows, (e)=>e.oc_releasedate ? formatDate(e.oc_releasedate, 'yyyy-mm-dd') : 'Unreleased');
    byDay = sorted(entries(byDay), (e)=>e[0], true);
    let days = byDay.map((day_plus_titles)=>{
        let [day, titles] = day_plus_titles;
        let items = titles.map((e)=>{
            return /*#__PURE__*/ _jsx(ListItem, {
                data: e
            }, e.otid);
        });
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx("h3", {
                    children: day === 'Unreleased' ? day : formatDate(day)
                }),
                /*#__PURE__*/ _jsxs(Item.Group, {
                    divided: true,
                    children: [
                        items,
                        " "
                    ]
                }),
                /*#__PURE__*/ _jsx(Divider, {})
            ]
        }, day);
    });
    return days;
};
export const ReleaseHistory = funcPart(({ request, dconfig, releaseHistory, partcfg, i18n })=>{
    if (!releaseHistory) {
        return /*#__PURE__*/ _jsx(Loader, {
            size: "small",
            inline: "centered",
            active: true
        });
    }
    if (size(releaseHistory) > 0) {
        if (false) {
            return /*#__PURE__*/ _jsx(DayGroupedTitles, {
                rows: releaseHistory
            });
        } else {
            return /*#__PURE__*/ _jsx(TitleGrid, {
                data: releaseHistory,
                grid: !!partcfg.grid
            });
        }
    } else {
        let limit = request.limit || partcfg.limit || dconfig.templating.releaseHistory.daylimit;
        return /*#__PURE__*/ _jsx("div", {
            children: i18n.noEntriesAvailableNow.replace('XNUM', limit)
        });
    }
});
const Navigator = connect('Navigator', 'meta.request', ({ request, navigator, onChange })=>{
    let { offset = 0 } = request;
    let numNavPages = parseInt(navigator.total / navigator.max);
    let onPageChange = (event, data)=>{
        let offset = (data.activePage - 1) * navigator.max;
        if (offset == 0) {
            offset = null;
        }
        if (onChange) {
            onChange(offset);
        }
    };
    return /*#__PURE__*/ _jsx(Pagination, {
        attached: true,
        activePage: Math.ceil((offset + 1) / navigator.max),
        totalPages: numNavPages,
        onPageChange: onPageChange,
        ellipsisItem: {
            content: /*#__PURE__*/ _jsx(Icon, {
                name: "ellipsis horizontal"
            }),
            icon: true
        },
        firstItem: {
            content: /*#__PURE__*/ _jsx(Icon, {
                name: "angle double left"
            }),
            icon: true
        },
        lastItem: {
            content: /*#__PURE__*/ _jsx(Icon, {
                name: "angle double right"
            }),
            icon: true
        },
        prevItem: {
            content: /*#__PURE__*/ _jsx(Icon, {
                name: "angle left"
            }),
            icon: true
        },
        nextItem: {
            content: /*#__PURE__*/ _jsx(Icon, {
                name: "angle right"
            }),
            icon: true
        }
    });
});
export const AllDocuments = funcPart({
    className: 'attached',
    partStyle: {
        overflow: 'hidden'
    }
}, ({ allDocuments = [], navigator, facets, request, go })=>{
    if (!navigator) {
        return /*#__PURE__*/ _jsx(Loader, {
            size: "small",
            inline: "centered",
            active: true
        });
    }
    let [offsetData, setOffsetData] = useState({});
    let [loadingOffset, setLoadingOffset] = useState(0);
    let [offsetDataKey, setOffsetDataKey] = useState(null);
    let [atBottom, setAtBottom] = useState(false);
    let contextRef = useRef(null);
    let { dname, facets: requestFacets, max, offset } = request;
    let nextOffsetDataKey = `${dname || ''}_${requestFacets || ''}_${max || ''}`;
    useEffect(()=>{
        if (offsetDataKey === nextOffsetDataKey) {
            return;
        }
        setOffsetDataKey(nextOffsetDataKey);
        setOffsetData({});
        if (contextRef.current) {
            contextRef.current.scrollTop = 0;
        }
    }, [
        offsetDataKey,
        nextOffsetDataKey
    ]);
    useEffect(()=>{
        if (loadingOffset && loadingOffset === offset) {
            setLoadingOffset(0);
        }
    }, [
        loadingOffset,
        offset
    ]);
    let buildItems = (allDocs)=>allDocs.map((e)=>/*#__PURE__*/ _jsx(ListItem, {
                data: e
            }, e.otid));
    let offsets = keys(offsetData).filter((e)=>e != (request.offset || 0));
    let oldAllDocs = flatten(sorted(offsets).map((o)=>buildItems(offsetData[o])));
    let allItems = [
        ...oldAllDocs,
        buildItems(allDocuments)
    ];
    if (loadingOffset) {
        allItems.push(/*#__PURE__*/ _jsx(Item, {
            size: "tiny",
            children: /*#__PURE__*/ _jsx(Loader, {
                active: true,
                inline: "centered"
            })
        }, "loader"));
    }
    let flex = {
        //~ width: '100%',
        height: '100%'
    };
    let onNavigator = (offset)=>{
        go({
            updateQuery: {
                offset
            }
        }, {
            history: 'replace'
        });
        setOffsetData({});
        setLoadingOffset(0);
        if (contextRef.current) {
            contextRef.current.scrollTop = 0;
        }
    };
    let handleScroll = (e)=>{
        let { scrollHeight, scrollTop, clientHeight } = e.target;
        let bottomDelta = Math.abs(scrollHeight - scrollTop - clientHeight);
        if (atBottom && bottomDelta > 50) {
            setAtBottom(false);
        } else if (!atBottom && bottomDelta < 5) {
            let offset = navigator.offset || 0;
            let nextOffset = offset + navigator.max;
            if (nextOffset < navigator.total && nextOffset !== loadingOffset) {
                //~ log('bottom %o', {loadingOffset, bottomDelta})
                setOffsetData({
                    ...offsetData,
                    [offset]: allDocuments
                });
                setLoadingOffset(nextOffset);
                setAtBottom(true);
                go({
                    updateQuery: {
                        offset: nextOffset
                    }
                }, {
                    history: 'replace'
                });
            }
        }
    };
    let CSS_MAGIC = 97;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Navigator, {
                navigator: navigator,
                onChange: onNavigator
            }),
            /*#__PURE__*/ _jsxs("div", {
                ref: contextRef,
                style: {
                    ...flex,
                    overflowY: 'auto',
                    overflowX: 'hidden'
                },
                onScroll: handleScroll,
                children: [
                    /*#__PURE__*/ _jsx(Rail, {
                        internal: true,
                        position: "right",
                        style: {
                            marginRight: 'var(--vl_scrollbar_width)',
                            paddingRight: 0
                        },
                        children: /*#__PURE__*/ _jsx(Sticky, {
                            context: contextRef.current,
                            offset: CSS_MAGIC,
                            children: /*#__PURE__*/ _jsx(Facets, {
                                facets: facets
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(Segment, {
                        attached: true,
                        style: {
                            flex: 1
                        },
                        children: /*#__PURE__*/ _jsx(Item.Group, {
                            divided: true,
                            children: allItems
                        })
                    })
                ]
            })
        ]
    });
});
export const TestNavbox = funcPart(({ navboxData, dname, go })=>{
    let dname2ddata = useSelector((root)=>root.config.domains.data);
    let buildDomain = (item)=>/*#__PURE__*/ _jsx(Menu.Item, {
            as: "a",
            name: dname2ddata[item.name].description,
            active: item.name === dname,
            onClick: ()=>go({
                    dname: item.name,
                    id: null
                })
        }, `domain_${item.name}`);
    if (!navboxData) {
        return /*#__PURE__*/ _jsx(Loader, {
            size: "small",
            inline: "centered",
            active: true
        });
    }
    let { domains } = navboxData;
    let items = domains.map((item)=>buildDomain(item));
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs(Menu, {
            pointing: true,
            secondary: true,
            vertical: true,
            children: [
                /*#__PURE__*/ _jsx(BoxHeaderIcon, {
                    name: "Domains"
                }),
                items
            ]
        })
    });
});
